import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { QuoteProvider, useQuote } from "@/context/QuoteContext";
import api from "@/api";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import Login from "@/components/Login";
import Header from "@/components/Header";
import ModeSelector from "@/components/ModeSelector";
import QuotesHistory from "@/components/QuotesHistory";
import WhatsAppPreview from "@/components/WhatsAppPreview";
import ObjectsWorkArea from "@/components/ObjectsWorkArea";
import AreaWorkArea from "@/components/AreaWorkArea";
import TextWorkArea from "@/components/TextWorkArea";
import ImageWorkArea from "@/components/ImageWorkArea";
import SimpleChatWorkArea from "@/components/SimpleChatWorkArea";
import PhotoInventory from "@/components/PhotoInventory";
import GuideDialog from "@/components/GuideDialog";
import GuidePrompt from "@/components/GuidePrompt";
import SettingsPanel from "@/components/SettingsPanel";
import WizardMode from "@/components/WizardMode";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Wand2 } from "lucide-react";

const baseModeOf = (m) => {
  if (m === "area" || m === "area_done") return "area";
  if (m === "text" || m === "text_done") return "text";
  if (m === "image" || m === "image_done") return "image";
  return "objects";
};

const WorkArea = ({ mode }) => {
  if (mode === "area") return <AreaWorkArea />;
  if (mode === "text" || mode === "text_done") return <TextWorkArea />;
  if (mode === "text_simple") return <SimpleChatWorkArea />;
  if (mode === "image") return <ImageWorkArea />;
  if (mode === "image_done") return <div className="space-y-5 fade-in"><PhotoInventory /><ObjectsWorkArea /></div>;
  return <ObjectsWorkArea />;
};

function Shell() {
  const { user, authLoading, mode, settings, currentQuoteId, newQuote, loadSavedQuote } = useQuote();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [promptMode, setPromptMode] = useState(null);
  const [wizardMode, setWizardMode] = useState(null);
  const prevMode = useRef(null);
  const guideManual = useRef(false);
  const deepLinkDone = useRef(false);

  // Deep link: open a specific stima passed as ?quote=<id> (used by AI usage details links)
  useEffect(() => {
    if (!user || deepLinkDone.current) return;
    const params = new URLSearchParams(window.location.search);
    const qid = params.get("quote");
    if (!qid) return;
    deepLinkDone.current = true;
    (async () => {
      try {
        const { data } = await api.get(`/quotes/${qid}`);
        if (data) loadSavedQuote(data);
      } catch { /* quote not found or no access */ }
      const url = new URL(window.location.href);
      url.searchParams.delete("quote");
      window.history.replaceState({}, "", url.pathname + url.search);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const optOutKey = () => (user ? `stormi_guide_optout_${user.id}` : null);
  const isGuideOptedOut = () => { const k = optOutKey(); return !!k && localStorage.getItem(k) === "1"; };
  const setGuideOptedOut = () => { const k = optOutKey(); if (k) localStorage.setItem(k, "1"); };
  const openGuideManual = () => { guideManual.current = true; setGuideOpen(true); };

  // start a fresh quote in a mode and open the wizard (from ModeSelector)
  const openWizardFresh = (m) => {
    if (user) localStorage.setItem(`stormi_guided_${m}_${user.id}`, "1");
    newQuote(m);
    setWizardMode(m);
  };
  // open the wizard for the quote already in progress (from work area)
  const openWizardCurrent = () => setWizardMode(baseModeOf(mode));

  useEffect(() => {
    if (!user) return;
    const key = `stormi_guide_seen_v1_${user.id}`;
    if (!localStorage.getItem(key) && !isGuideOptedOut()) { guideManual.current = false; setGuideOpen(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ask, once per mode, whether the user wants a step-by-step guided quote
  useEffect(() => {
    if (!user) { prevMode.current = mode; return; }
    const base = ["objects", "area", "image", "text"];
    const isFresh = base.includes(mode) && prevMode.current !== mode && !currentQuoteId;
    prevMode.current = mode;
    if (!isFresh || isGuideOptedOut()) return;
    const key = `stormi_guided_${mode}_${user.id}`;
    if (!localStorage.getItem(key)) setPromptMode(mode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, currentQuoteId, user]);

  const markGuidedAsked = (m) => { if (user) localStorage.setItem(`stormi_guided_${m}_${user.id}`, "1"); };
  const acceptGuided = () => { markGuidedAsked(promptMode); setPromptMode(null); guideManual.current = true; setGuideOpen(true); };
  const declineGuided = () => { markGuidedAsked(promptMode); setGuideOptedOut(); setPromptMode(null); };

  const closeGuide = (o) => {
    setGuideOpen(o);
    if (!o && user) {
      localStorage.setItem(`stormi_guide_seen_v1_${user.id}`, "1");
      // Dismissing the auto-shown welcome tour counts as opting out of future auto-offers
      if (!guideManual.current) setGuideOptedOut();
    }
  };

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#1a5f5a]" /></div>;
  }
  if (!user) return <Login />;

  return (
    <div className="min-h-screen flex flex-col">
      <Header onOpenSettings={() => setSettingsOpen(true)} onOpenGuide={openGuideManual} />
      <main className="flex-1 max-w-[1150px] mx-auto w-full px-4 sm:px-6 lg:px-8 pt-6 pb-12">
        {mode === null ? (
          <div className="fade-in">
            <ModeSelector onOpenWizard={openWizardFresh} />
            <QuotesHistory />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8 items-start">
            <div className="space-y-4">
              {mode !== "text_simple" && (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-[#1a5f5a]/20 bg-[#1a5f5a]/5 px-4 py-3" data-testid="wizard-launch-bar">
                <div className="flex items-center gap-2.5 min-w-0">
                  <Wand2 className="h-5 w-5 text-[#1a5f5a] shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-700">Modalità guidata</div>
                    <div className="text-xs text-slate-500 truncate">Ti accompagno passo-passo fino al messaggio pronto.</div>
                  </div>
                </div>
                <Button size="sm" onClick={openWizardCurrent} className="bg-[#1a5f5a] hover:bg-[#134744] gap-1.5 shrink-0" data-testid="wizard-launch-button">
                  <Wand2 className="h-4 w-4" /> Avvia
                </Button>
              </div>
              )}
              <WorkArea mode={mode} />
            </div>
            <WhatsAppPreview />
          </div>
        )}
      </main>

      <Footer />

      {wizardMode && <WizardMode baseMode={wizardMode} onClose={() => setWizardMode(null)} />}
      <GuideDialog open={guideOpen} onOpenChange={closeGuide} mode={mode} settingsOpen={settingsOpen} />
      <GuidePrompt open={!!promptMode} mode={promptMode} onYes={acceptGuided} onNo={declineGuided} />
      {settingsOpen && settings && <SettingsPanel onClose={() => setSettingsOpen(false)} onOpenGuide={openGuideManual} />}
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default function App() {
  return (
    <QuoteProvider>
      <Shell />
    </QuoteProvider>
  );
}
